import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { ErrorMessages } from './constants/errormessages.constants';
import { LoaderMessages } from './constants/loadermessages.constants';
import { CommonService } from './services/common.service';
import { DisplayMessages } from './model/common.model';

type canActivateType = Promise<boolean | UrlTree>;

export const setUserDetails = async () => {
  const commonService: CommonService = inject(CommonService);
  const router = inject(Router);
  let isUserLoggedIn = true;
  if (!commonService.username) {
    commonService.showLoader(LoaderMessages.UserDetails);
    await commonService
      .login()
      .then(async (res: any) => {
        res = res.data;
        localStorage.setItem('role', res.role);
        localStorage.setItem('username', res.username);
        commonService.role = res.role;
        commonService.username = res.username;
        commonService.setSecurityPledgeSign(res.security_pledge_sign);
        isUserLoggedIn = res.role ? true : false;
        commonService.hideLoader('Fetched user details', false);
      })
      .catch((error: any) => {
        const errMsg = error?.['error']?.['message'];
        const token = localStorage.getItem('token');
        if (commonService.isTokenExpired(token)) {
          commonService.hideLoader(ErrorMessages.TokenExpired, true);
          router.navigate(['login']);
        } else commonService.hideLoader(errMsg ? errMsg : ErrorMessages.UserDetailsFetchError, true);
        isUserLoggedIn = false;
      });
  }
  return isUserLoggedIn;
};
export const unregisteredUser = async (): Promise<boolean> => {
  const commonService = inject(CommonService);
  const router = inject(Router);
  let isRegisteredUser = false;
  if (!commonService.username) {
    commonService.showLoader(LoaderMessages.UserDetails);
    await setUserDetails();
    if (!commonService.role) {
      await commonService
        .getApprovalStatus(commonService.username)
        .then((res: any) => {
          if (res['response'] === DisplayMessages.InProgress) {
            commonService.approvalSentStatusCheck = true;
            commonService.approvalStatus = DisplayMessages.InProgress;
          }
          commonService.hideLoader('Fetched user details', false);
          isRegisteredUser = false;
        })
        .catch((error: any) => {
          const errMsg = error?.['error']?.['message'];
          const token = localStorage.getItem('token');
          if (commonService.isTokenExpired(token)) {
            commonService.hideLoader(ErrorMessages.TokenExpired, true);
            router.navigate(['login']);
          } else commonService.hideLoader(errMsg ? errMsg : ErrorMessages.UserDetailsFetchError, true);
          isRegisteredUser = false;
        });
    }
  }
  return isRegisteredUser;
};
export const UserRegistrationRouteGuard = async () => {
  const router = inject(Router);
  const commonService = inject(CommonService);
  if (unsupportedDevice()) {
    await unregisteredUser();
    if (commonService.role) {
      router.navigate(['']);
    }
    return true;
  } else {
    router.navigate(['unsupported']);
    return false;
  }
};
export const UnsupportedDeviceRouterGuard = (): boolean | canActivateType => {
  const router = inject(Router);
  if (unsupportedDevice()) {
    router.navigate(['']);
  }
  return true;
};
export const unsupportedDevice = (): boolean => {
  const userAgent: string = window['navigator']['userAgent'];
  let flag = true;
  if ((userAgent.indexOf('Opera') || userAgent.indexOf('OPR')) != -1) {
    flag = false;
  } else if (userAgent.indexOf('Edg') != -1) {
    flag = true;
  } else if (userAgent.indexOf('Chrome') != -1) {
    flag = true;
  } else if (userAgent.indexOf('Safari') != -1) {
    flag = false;
  } else if (userAgent.indexOf('Firefox') != -1) {
    flag = false;
  } else if (userAgent.indexOf('MSIE') != -1) {
    flag = false;
  } else {
    flag = false;
  }
  const isMobile =
    userAgent.includes('Android') ||
    userAgent.includes('iPhone') ||
    userAgent.includes('BlackBerry') ||
    userAgent.includes('iPad')
      ? true
      : false;

  return flag && !isMobile;
};
export const LoginGuard = (): boolean | canActivateType => {
  const router = inject(Router);
  if (unsupportedDevice()) {
    if (localStorage.getItem('token')) router.navigate(['']);
    return true;
  } else {
    router.navigate(['unsupported']);
    return false;
  }
};
export const RouterGuard: CanActivateFn = async (route: ActivatedRouteSnapshot): canActivateType => {
  const router = inject(Router);
  const commonService = inject(CommonService);
  if (unsupportedDevice()) {
    const token: string | null = localStorage.getItem('token');
    if (token) {
      if (commonService.isTokenExpired(token)) {
        localStorage.clear();
        commonService.role = '';
        commonService.username = '';
        commonService.displayError('Session expired. Please login');
        router.navigate(['login']);
        return false;
      } else {
        if (!commonService.username) {
          await setUserDetails();
          if (!commonService.role) {
            await commonService
              .getApprovalStatus(commonService.username)
              .then((res: any) => {
                if (res['response'] === DisplayMessages.InProgress) {
                  commonService.approvalSentStatusCheck = true;
                  commonService.approvalStatus = DisplayMessages.InProgress;
                }

                router.navigate(['knox']);
              })
              .catch((error: any) => {
                const errMsg = error?.['error']?.['message'];
                const token = localStorage.getItem('token');
                if (commonService.isTokenExpired(token)) {
                  commonService.hideLoader(ErrorMessages.TokenExpired, true);
                  router.navigate(['login']);
                } else commonService.hideLoader(errMsg ? errMsg : ErrorMessages.UserDetailsFetchError, true);
                router.navigate(['knox']);
              });
          } else {
            if (route.data['roles'].includes(commonService.role)) {
              const queryParams = route.queryParams;
              router.navigate([route.url[0].path], { queryParams });
            } else {
              router.navigate(['']);
            }
          }
        }
        return true;
      }
    } else {
      const ssoQueryParam: boolean = route.queryParamMap.has('sso');
      if (ssoQueryParam) {
        if (route.fragment?.includes('id_token')) {
          const newToken: string = route.fragment.split('=')[1];
          localStorage.setItem('token', newToken);
          await setUserDetails();
          if (!commonService.role) {
            await commonService
              .getApprovalStatus(commonService.username)
              .then((res: any) => {
                if (res['response'] === DisplayMessages.InProgress) {
                  commonService.approvalSentStatusCheck = true;
                  commonService.approvalStatus = DisplayMessages.InProgress;
                }
                router.navigate(['knox']);
              })
              .catch((error: any) => {
                const errMsg = error?.['error']?.['message'];
                const token = localStorage.getItem('token');
                if (commonService.isTokenExpired(token)) {
                  commonService.hideLoader(ErrorMessages.TokenExpired, true);
                  router.navigate(['login']);
                } else commonService.hideLoader(errMsg ? errMsg : ErrorMessages.UserDetailsFetchError, true);
                router.navigate(['knox']);
              });
          } else {
            if (route.data['roles'].includes(commonService.role)) {
              const returnUrl = localStorage.getItem('returnUrl');
              if (returnUrl) {
                localStorage.removeItem('returnUrl');
                window.location.href = returnUrl;
              } else {
                const queryParams = route.queryParams;
                router.navigate([route.url[0].path], { queryParams });
              }
            } else {
              router.navigate(['']);
            }
          }
        }
        return true;
      } else {
        const returnUrl = window.location.href;
        router.navigate(['login'], { queryParams: { returnUrl: returnUrl } });
        return false;
      }
    }
  } else {
    router.navigate(['unsupported']);
    return false;
  }
};
